import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Loader2, Copy, Check, AlertCircle, Percent, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import IngredientSearch from "../components/IngredientSearch";
import IngredientsTable from "../components/IngredientsTable";
import SummaryWidget from "../components/SummaryWidget";
import SuggestPanel from "../components/SuggestPanel";
import AgroFieldsPanel from "../components/AgroFieldsPanel";
import IngredientEditDialog from "../components/IngredientEditDialog";
import { createRecipe, getRecipe, updateRecipe, deleteRecipe, api } from "../lib/api";
import { detectQuidCandidates } from "../lib/quid";
import { toast } from "sonner";

const newIngredientFromFood = (food, quantity = 100, nameFr = null) => ({
  id: crypto.randomUUID(),
  fdc_id: food.fdc_id ?? null,
  blv_dbid: food.blv_dbid ?? null,
  source: food._source || (food._custom ? "custom" : "USDA"),
  description: food.description,
  brand: food.brand || null,
  name_fr: nameFr || food._name_fr || null,
  quantity_g: quantity,
  custom: !!food._custom,
  nutrients_per_100g: food.nutrients_per_100g,
});

export const RecipeBuilder = () => {
  const { id } = useParams();
  const isNew = !id || id === "nouvelle";
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [sku, setSku] = useState("");
  const [allergens, setAllergens] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // ingredient being edited, or null
  const [createOpen, setCreateOpen] = useState(false);
  const [inputMode, setInputMode] = useState("g"); // "g" | "pct"
  const [targetTotal, setTargetTotal] = useState(0);

  const switchInputMode = (mode) => {
    if (mode === "pct") {
      const sum = ingredients.reduce((s, i) => s + (i.quantity_g || 0), 0);
      setTargetTotal(sum > 0 ? +sum.toFixed(0) : 100);
    }
    setInputMode(mode);
  };

  const handleTargetTotalChange = (newTotal) => {
    // Rescale all ingredients proportionally so each kept its % when target changes
    if (newTotal > 0 && targetTotal > 0) {
      const factor = newTotal / targetTotal;
      setIngredients((prev) =>
        prev.map((i) => ({ ...i, quantity_g: +((i.quantity_g || 0) * factor).toFixed(2) }))
      );
    }
    setTargetTotal(newTotal);
  };

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const recipe = await getRecipe(id);
        setName(recipe.name);
        setNotes(recipe.notes || "");
        setSku(recipe.sku || "");
        setAllergens(recipe.allergens || []);
        setIngredients(recipe.ingredients || []);
      } catch {
        toast.error("Recette introuvable.");
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate]);

  const addFromUSDA = (food, quantity = 100, nameFr = null) => {
    setIngredients((prev) => [...prev, newIngredientFromFood(food, quantity, nameFr)]);
    toast.success("Ingrédient ajouté.");
  };

  const changeQty = (ingId, qty) => {
    setIngredients((prev) =>
      prev.map((i) => (i.id === ingId ? { ...i, quantity_g: qty } : i))
    );
  };

  const removeIngredient = (ingId) => {
    setIngredients((prev) => prev.filter((i) => i.id !== ingId));
  };

  const toggleQuid = (ingId) => {
    setIngredients((prev) =>
      prev.map((i) => (i.id === ingId ? { ...i, quid: !i.quid } : i))
    );
  };

  const applyEdit = (patch) => {
    if (!editTarget) return;
    setIngredients((prev) =>
      prev.map((i) => (i.id === editTarget.id ? { ...i, ...patch } : i))
    );
    toast.success("Ingrédient mis à jour.");
  };

  const addCustom = (newIng) => {
    setIngredients((prev) => [...prev, newIng]);
    toast.success("Ingrédient personnalisé ajouté.");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Donnez un nom à votre recette.");
      return;
    }
    if (!sku.trim()) {
      toast.error("Sélectionnez d'abord un SKU AgroManage.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        notes,
        sku: sku.trim(),
        allergens,
        ingredients,
      };
      if (isNew) {
        const created = await createRecipe(payload);
        toast.success("Recette créée !");
        navigate(`/recettes/${created.id}`);
      } else {
        await updateRecipe(id, payload);
        toast.success("Recette enregistrée.");
      }
    } catch (e) {
      toast.error("Erreur d'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRecipe(id);
      toast.success("Recette supprimée.");
      navigate("/");
    } catch {
      toast.error("Erreur de suppression.");
    }
  };

  const handleCopyForAgroManage = async () => {
    if (isNew) {
      toast.error("Enregistrez d'abord la recette pour pouvoir la copier.");
      return;
    }
    if (!sku) {
      toast.error("Sélectionnez d'abord un SKU AgroManage.");
      return;
    }
    try {
      const { data: payload } = await api.get(`/agromanage/recipes/${id}`);
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
      toast.success("JSON copié. Collez-le dans AgroManage.");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Impossible de copier.");
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-12 flex items-center justify-center text-stone-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-8 md:p-12" data-testid="recipe-builder-page">
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="text-stone-600 hover:text-stone-900 hover:bg-stone-100 -ml-3"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleCopyForAgroManage}
            disabled={ingredients.length === 0}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
            data-testid="copy-agromanage-btn"
            title="Copie un JSON (valeurs pour 100 g + total) à coller dans AgroManage"
          >
            {copied ? (
              <Check className="h-4 w-4 mr-2" />
            ) : (
              <Copy className="h-4 w-4 mr-2" />
            )}
            {copied ? "Copié !" : "Copier pour AgroManage"}
          </Button>
          {!isNew && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  data-testid="delete-recipe-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer cette recette ?</AlertDialogTitle>
                  <AlertDialogDescription>Action définitive.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-delete-builder"
                  >
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || !sku.trim()}
            className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold tracking-wide rounded-lg h-10 px-5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-recipe-btn"
            title={!sku.trim() ? "Sélectionnez d'abord un SKU AgroManage" : ""}
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isNew ? "Créer la recette" : "Enregistrer"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main column */}
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-emerald-700 mb-3">
              {isNew ? "Nouvelle recette" : "Édition"}
            </div>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nom de la recette (ex: Bowl quinoa-poulet)"
              className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight border-0 border-b border-stone-200 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-emerald-600 h-auto shadow-none placeholder:text-stone-300"
              data-testid="recipe-name-input"
            />
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes, instructions, portion…"
              rows={2}
              className="mt-4 border-stone-200 focus-visible:ring-emerald-500 resize-none"
              data-testid="recipe-notes-input"
            />
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-stone-900">
                Ajouter un ingrédient
              </h2>
              <span className="text-xs text-stone-400 uppercase tracking-widest font-bold">
                BLV · USDA
              </span>
            </div>
            <IngredientSearch onSelect={addFromUSDA} />
            <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
              <p className="text-xs text-stone-500">
                🇨🇭 Base suisse (français) par défaut · 🇺🇸 USDA en repli pour les ingrédients exotiques (anglais).
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreateOpen(true)}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                data-testid="add-custom-ingredient-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Ingrédient personnalisé
              </Button>
            </div>
          </div>

          <SuggestPanel dishName={name} onPickIngredient={addFromUSDA} />

          <AgroFieldsPanel
            sku={sku}
            onSkuChange={setSku}
            allergens={allergens}
            onAllergensChange={setAllergens}
            ingredients={ingredients}
          />

          <div>
            <h2 className="font-heading text-xl font-bold text-stone-900 mb-3">
              Ingrédients de la recette
            </h2>

            {(() => {
              const candidates = detectQuidCandidates(name, ingredients);
              if (candidates.length === 0) return null;
              return (
                <div
                  className="mb-3 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl"
                  data-testid="quid-suggestion-banner"
                >
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-amber-900">
                      Rappel INCO : QUID recommandé
                    </div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      Le nom de la recette mentionne ces ingrédients — leur pourcentage devrait
                      apparaître sur l&apos;étiquette :
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {candidates.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleQuid(c.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-amber-300 text-amber-900 text-xs font-semibold hover:bg-amber-100 transition-colors"
                          data-testid={`quid-suggestion-${c.id}`}
                        >
                          <Percent className="h-3 w-3" />
                          Activer QUID sur « {c.label} »
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            <IngredientsTable
              ingredients={ingredients}
              onChangeQty={changeQty}
              onRemove={removeIngredient}
              onToggleQuid={toggleQuid}
              onEdit={setEditTarget}
              inputMode={inputMode}
              onInputModeChange={switchInputMode}
              targetTotal={targetTotal}
              onTargetTotalChange={handleTargetTotalChange}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4">
          <SummaryWidget ingredients={ingredients} recipeName={name} />
        </div>
      </div>

      <IngredientEditDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        onSubmit={addCustom}
      />
      <IngredientEditDialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        mode="edit"
        ingredient={editTarget}
        onSubmit={applyEdit}
      />
    </div>
  );
};

export default RecipeBuilder;
